import { AppCopy } from '@models/cms/app.model';
import { BagsCopy } from '@models/cms/bags.model';
import {HomeCopy, HomeFlashSale} from '@models/cms/home.model';
import { SeatsCopy } from '@models/cms/seats.model';
import { MealsCopy } from '@models/cms/meals.model';
import { ExtrasCopy } from '@models/cms/extras.model';
import { PassengerCopy } from '@models/cms/passenger.model';
import {
  CorporateCreditCardsCopy,
  PaymentCopy,
  PriceDisplayPayCopy,
} from '@models/cms/payment.model';
import { PurchaseConfirmationCopy } from '@models/cms/purchase-confirmation.model';
import { FlightsCopy } from '@models/cms/flights.model';
import { TripDetailsCopy } from '@models/cms/trip-details.model';
import { FindTripsCopy } from '@models/cms/find-trips.model';
import {
  cmsCopyLoaded,
  cmsDynamicCopyLoaded,
  resetCms, updateActiveDynamicPage,
} from '@actions/cms.action';
import { Action, createReducer, on } from '@ngrx/store';
import { BookCopy } from '@models/cms/book.model';
import { TripDetailsFlightStatusCopy } from '@models/cms/trip-details-flight-status.model';
import { BoardingPassCopy } from '@models/cms/boarding-pass-model';
import {
  CmsDynamicFeatureCopy,
  CmsDynamicFlows,
  CmsDynamicKeys,
  CmsDynamicSubFlows,
  CmsKeys,
  ContentPageKeys,
  DYNAMIC,
  FeaturePageKeys,
} from '@constants/cms.constants';
import {
  CorporateResponsibilityCopy,
  ManagementTeamCopy,
  SustainabilityCopy,
} from '@models/cms/cms-dynamic/corporate/about/about.model';
import { MediaLibraryCopy } from '@models/cms/cms-dynamic/corporate/media/media.model';
import {
  AnalystsCoverageCopy,
  FinancialCalendarCopy,
  FinancialReportsCopy,
  PresentationsCopy,
  ShareHoldersCopy,
} from '@models/cms/cms-dynamic/corporate/investors/investors.model';
import {
  EconomyCopy,
  PremiumCopy,
} from '@models/cms/cms-dynamic/experience/cabins/cabins.model';
import { DreamLinersCopy } from '@models/cms/cms-dynamic/experience/experience.model';
import {
  HelpCenterCopy,
  LegalInformationCopy,
  OptionalServiceFeesCopy,
} from '@models/cms/cms-dynamic/support/support.model';
import { WhereWeFlyCopy } from '@models/cms/cms-dynamic/experience/where-we-fly.model';
import { NotFoundCopy } from '@models/cms/not-found.model';
import { DynamicSubNav } from '@models/cms/cms-dynamic/dynamic-sub-nav.model';
import {DynamicPageCopy, PromoDealsElement} from '@models/cms/cms-dynamic/dynamic-page.model';
import { BidForUpgradeCopy } from '@models/cms/cms-dynamic/experience/bid-for-upgrade.model';
import { TsaPrecheckCopy } from '@models/cms/cms-dynamic/support/tsa-precheck.model';
import { AccountCopy } from '@models/cms/account.model';
import {AnnounceBanner} from "@models/cms/announce-banner.model";
import { CheckInMessageBoxCopy } from '@models/cms/check-in.model';
import { UpgradeCopy, UpgradePaymentCopy } from '@models/cms/upgrade.model';
import {
  MMBHomeCopy,
  MMBHomeV2Copy,
  MMBHomeV3Copy,
  MMBHomeV4Copy,
  MMBHomeV6Copy,
} from '@models/cms/mmb-home.model';
import { DeeplinksCopy } from '@models/cms/deeplinks.model';
import { FareAvailabilityRibbonTilesCopy } from '@models/cms/fare-availability-ribbon.model';
import { AppDownloadBannerCopy } from '@models/cms/app-download-banner-model';
import { NavbarCopy } from '@models/cms/header.model';
import { ClarifyingFinalStepOCICopy } from 'src/app/features/confirmation/confirmation.model';

export interface State {
  // flows
  [CmsKeys.Account]: AccountCopy | null;
  [CmsKeys.App]: AppCopy | null;
  [CmsKeys.Book]: BookCopy | null;
  [CmsKeys.Cancel]: BookCopy | null;
  [CmsKeys.CheckIn]: BookCopy | null;
  [CmsKeys.Home]: HomeCopy | null;
  [CmsKeys.Modify]: BookCopy | null;
  [CmsKeys.Pay]: BookCopy | null;
  [CmsKeys.Trips]: FindTripsCopy | null;
  [CmsKeys.Upgrade]: BookCopy | null;
  // pages
  // eslint-disable-next-line @typescript-eslint/member-ordering
  [CmsKeys.Bags]: BagsCopy | null;
  [CmsKeys.BoardingPass]: BoardingPassCopy | null;
  [CmsKeys.Confirmation]: PurchaseConfirmationCopy | null;
  [CmsKeys.ContactInfo]: PassengerCopy | null;
  [CmsKeys.Documents]: TripDetailsCopy | null;
  [CmsKeys.Extras]: ExtrasCopy | null;
  [CmsKeys.Flights]: FlightsCopy | null;
  [CmsKeys.FlightStatus]: TripDetailsFlightStatusCopy | null;
  [CmsKeys.Meals]: MealsCopy | null;
  [CmsKeys.NotFound]: NotFoundCopy | null;
  [CmsKeys.Payment]: PaymentCopy | null;
  [CmsKeys.PersonalInfo]: PassengerCopy | null;
  [CmsKeys.PurchaseCancellation]: PurchaseConfirmationCopy | null;
  [CmsKeys.Receipt]: TripDetailsCopy | null;
  [CmsKeys.Review]: PaymentCopy | null;
  [CmsKeys.Seats]: SeatsCopy | null;
  [CmsKeys.TravelDocuments]: PassengerCopy | null;
  [CmsKeys.Travelers]: PassengerCopy | null;
  [CmsKeys.TravelPreferences]: PassengerCopy | null;
  [CmsKeys.TripDetails]: TripDetailsCopy | null;

  //Umbraco Content pages
  [ContentPageKeys.AnnounceBanners]: {announceBanners: AnnounceBanner[]} | null;
  [ContentPageKeys.CheckInMessageBox]: CheckInMessageBoxCopy | null;
  [ContentPageKeys.Skybid]: UpgradeCopy | null;
  [ContentPageKeys.SkybidPayment]: UpgradePaymentCopy | null;
  [ContentPageKeys.Deals]: {promoDeals: PromoDealsElement[]} | null;
  [ContentPageKeys.FlashSales]: {flashSales: HomeFlashSale}| null;
  [ContentPageKeys.FareBrandCards]: FareAvailabilityRibbonTilesCopy | null;

  //Umbraco Feature pages
  [FeaturePageKeys.Deeplinks]: DeeplinksCopy | null;
  [FeaturePageKeys.PriceDisplayPayCopy]: PriceDisplayPayCopy | null;
  [FeaturePageKeys.CorporateCreditCards]: CorporateCreditCardsCopy | null;
  [FeaturePageKeys.SkybidMMBHomePageV1]: MMBHomeCopy | null;
  [FeaturePageKeys.SkybidMMBHomePageV2]: MMBHomeV2Copy | null;
  [FeaturePageKeys.SkybidMMBHomePageV3]: MMBHomeV3Copy | null;
  [FeaturePageKeys.SkybidMMBHomePageV4]: MMBHomeV4Copy | null;
  [FeaturePageKeys.SkybidMMBHomePageV6]: MMBHomeV6Copy | null;
  [FeaturePageKeys.AppDownloadBanner]: AppDownloadBannerCopy | null;
  [FeaturePageKeys.NavbarCopy]: NavbarCopy | null;
  [FeaturePageKeys.ClarifyingFinalStepOCI]: ClarifyingFinalStepOCICopy | null;
  
  pageCacheFlowMap: {
    [CmsKeys.Bags]: BagsCopy | null;
    [CmsKeys.BoardingPass]: BoardingPassCopy | null;
    [CmsKeys.Confirmation]: PurchaseConfirmationCopy | null;
    [CmsKeys.ContactInfo]: PassengerCopy | null;
    [CmsKeys.Documents]: TripDetailsCopy | null;
    [CmsKeys.Extras]: ExtrasCopy | null;
    [CmsKeys.Flights]: FlightsCopy | null;
    [CmsKeys.FlightStatus]: TripDetailsFlightStatusCopy | null;
    [CmsKeys.Meals]: MealsCopy | null;
    [CmsKeys.NotFound]: NotFoundCopy | null;
    [CmsKeys.Payment]: PaymentCopy | null;
    [CmsKeys.PersonalInfo]: PassengerCopy | null;
    [CmsKeys.PurchaseCancellation]: PurchaseConfirmationCopy | null;
    [CmsKeys.Receipt]: TripDetailsCopy | null;
    [CmsKeys.Review]: PaymentCopy | null;
    [CmsKeys.Seats]: SeatsCopy | null;
    [CmsKeys.TravelDocuments]: PassengerCopy | null;
    [CmsKeys.Travelers]: PassengerCopy | null;
    [CmsKeys.TravelPreferences]: PassengerCopy | null;
    [CmsKeys.TripDetails]: TripDetailsCopy | null;
  };

  // eslint-disable-next-line @typescript-eslint/member-ordering
  [DYNAMIC]: CmsDynamicState | null;
  activeDynamicPage: string | null;
}

export interface CmsDynamicState {
  [CmsDynamicFlows.Corporate]: CmsDynamicCorporateState | null;
  [CmsDynamicFlows.Experience]: CmsDynamicExperienceState | null;
  [CmsDynamicFlows.Menus]: DynamicPageCopy | null;
  [CmsDynamicFlows.Promo]: CmsDynamicPromoState | null;
  [CmsDynamicFlows.Support]: CmsDynamicSupportState | null;
}

export interface CmsDynamicCorporateState {
  subNav: DynamicSubNav | null;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  [CmsDynamicSubFlows.About]: CmsDynamicAboutState | null;
  [CmsDynamicSubFlows.Investors]: CmsDynamicInvestorsState | null;
  [CmsDynamicSubFlows.Media]: CmsDynamicMediaState | null;
}

export interface CmsDynamicExperienceState {
  subNav: DynamicSubNav | null;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  [CmsDynamicSubFlows.Cabins]: CmsDynamicExperienceCabinsState | null;
  [CmsDynamicSubFlows.Dreamliners]: DreamLinersCopy | null;
  [CmsDynamicSubFlows.Sustainability]: SustainabilityCopy | null;
  [CmsDynamicSubFlows.WhereWeFly]: WhereWeFlyCopy | null;
}

export interface CmsDynamicExperienceCabinsState {
  [CmsDynamicKeys.BidForUpgrade]: BidForUpgradeCopy | null;
  [CmsDynamicKeys.Economy]: EconomyCopy | null;
  [CmsDynamicKeys.Inflight]: DynamicPageCopy | null;
  [CmsDynamicKeys.Premium]: PremiumCopy | null;
}

export interface CmsDynamicPromoState {
  [CmsDynamicSubFlows.TermsAndConditions]: DynamicPageCopy | null;
}

export interface CmsDynamicSupportState {
  subNav: DynamicSubNav | null;
  // eslint-disable-next-line @typescript-eslint/member-ordering
  [CmsDynamicSubFlows.Baggage]: DynamicPageCopy | null;
  [CmsDynamicSubFlows.FareRules]: DynamicPageCopy | null;
  [CmsDynamicSubFlows.GlobalEntry]: DynamicPageCopy | null;
  [CmsDynamicSubFlows.HelpCenter]: HelpCenterCopy | null;
  [CmsDynamicSubFlows.Infants]: DynamicPageCopy | null;
  [CmsDynamicSubFlows.LegalInformation]: LegalInformationCopy | null;
  [CmsDynamicSubFlows.LinkedTravelAgreementCt]: DynamicPageCopy | null;
  [CmsDynamicSubFlows.OptionalServiceFees]: OptionalServiceFeesCopy | null;
  [CmsDynamicSubFlows.PreFlightCheckList]: DynamicPageCopy | null;
  [CmsDynamicSubFlows.RestrictedItems]: DynamicPageCopy | null;
  [CmsDynamicSubFlows.TsaPreCheck]: TsaPrecheckCopy | null;
}

export interface CmsDynamicAboutState {
  [CmsDynamicKeys.CompanyProfile]: DynamicPageCopy | null;
  [CmsDynamicKeys.CorporateResponsibility]: CorporateResponsibilityCopy | null;
  [CmsDynamicKeys.Imprint]: DynamicPageCopy | null;
  [CmsDynamicKeys.ManagementTeam]: ManagementTeamCopy | null;
  [CmsDynamicKeys.OurBrand]: DynamicPageCopy | null;
  [CmsDynamicKeys.OurValues]: DynamicPageCopy | null;
  [CmsDynamicKeys.Sustainability]: SustainabilityCopy | null;
}

export interface CmsDynamicMediaState {
  [CmsDynamicKeys.MediaLibrary]: MediaLibraryCopy | null;
}

export interface CmsDynamicInvestorsState {
  [CmsDynamicKeys.AnalystCoverage]: AnalystsCoverageCopy | null;
  [CmsDynamicKeys.CorporateGovernance]: FinancialReportsCopy | null;
  [CmsDynamicKeys.FinancialCalendar]: FinancialCalendarCopy | null;
  [CmsDynamicKeys.FinancialReports]: FinancialReportsCopy | null;
  [CmsDynamicKeys.Presentations]: PresentationsCopy | null;
  [CmsDynamicKeys.ShareHolders]: ShareHoldersCopy | null;
}

export const INITIAL_STATE: State = {
  // flows
  [CmsKeys.Account]: null,
  [CmsKeys.App]: null,
  [CmsKeys.Book]: null,
  [CmsKeys.Cancel]: null,
  [CmsKeys.CheckIn]: null,
  [CmsKeys.Home]: null,
  [CmsKeys.Modify]: null,
  [CmsKeys.Pay]: null,
  [CmsKeys.Trips]: null,
  [CmsKeys.Upgrade]: null,
  // pages

  [CmsKeys.Bags]: null,
  [CmsKeys.BoardingPass]: null,
  [CmsKeys.Confirmation]: null,
  [CmsKeys.ContactInfo]: null,
  [CmsKeys.Documents]: null,
  [CmsKeys.Extras]: null,
  [CmsKeys.Flights]: null,
  [CmsKeys.FlightStatus]: null,
  [CmsKeys.Meals]: null,
  [CmsKeys.NotFound]: null,
  [CmsKeys.Payment]: null,
  [CmsKeys.PersonalInfo]: null,
  [CmsKeys.PurchaseCancellation]: null,
  [CmsKeys.Receipt]: null,
  [CmsKeys.Review]: null,
  [CmsKeys.Seats]: null,
  [CmsKeys.TravelDocuments]: null,
  [CmsKeys.Travelers]: null,
  [CmsKeys.TravelPreferences]: null,
  [CmsKeys.TripDetails]: null,

  [ContentPageKeys.Skybid]: null,
  [ContentPageKeys.SkybidPayment]: null,
  [ContentPageKeys.AnnounceBanners]: null,
  [ContentPageKeys.Deals]: null,
  [ContentPageKeys.FlashSales]: null,
  [ContentPageKeys.CheckInMessageBox]: null,
  [ContentPageKeys.FareBrandCards]: null,
  [FeaturePageKeys.Deeplinks]: null,
  [FeaturePageKeys.PriceDisplayPayCopy]: null,
  [FeaturePageKeys.SkybidMMBHomePageV1]: null,
  [FeaturePageKeys.SkybidMMBHomePageV2]: null,
  [FeaturePageKeys.SkybidMMBHomePageV3]: null,
  [FeaturePageKeys.SkybidMMBHomePageV4]: null,
  [FeaturePageKeys.SkybidMMBHomePageV6]: null,
  [FeaturePageKeys.CorporateCreditCards]: null,
  [FeaturePageKeys.AppDownloadBanner]: null,
  [FeaturePageKeys.NavbarCopy]: null,
  [FeaturePageKeys.ClarifyingFinalStepOCI]: null,

  pageCacheFlowMap: {
    [CmsKeys.Bags]: null,
    [CmsKeys.BoardingPass]: null,
    [CmsKeys.Confirmation]: null,
    [CmsKeys.ContactInfo]: null,
    [CmsKeys.Documents]: null,
    [CmsKeys.Extras]: null,
    [CmsKeys.Flights]: null,
    [CmsKeys.FlightStatus]: null,
    [CmsKeys.Meals]: null,
    [CmsKeys.NotFound]: null,
    [CmsKeys.Payment]: null,
    [CmsKeys.PersonalInfo]: null,
    [CmsKeys.PurchaseCancellation]: null,
    [CmsKeys.Receipt]: null,
    [CmsKeys.Review]: null,
    [CmsKeys.Seats]: null,
    [CmsKeys.TravelDocuments]: null,
    [CmsKeys.Travelers]: null,
    [CmsKeys.TravelPreferences]: null,
    [CmsKeys.TripDetails]: null,
  },
  dynamic: null,
  activeDynamicPage: null,
};

const reducer = createReducer(
  INITIAL_STATE,

  on(
    cmsCopyLoaded,
    (state, action): State => ({
      ...state,
      [action.key || '']: action.data,
      pageCacheFlowMap: {
        ...state.pageCacheFlowMap,
        ...(action.flow
          ? {
              [action.key || '']: action.flow,
            }
          : {}),
      },
    }),
  ),

  on(cmsDynamicCopyLoaded, (state, action): State => {
    let subFlowData = {};
    if (action.key && action.subFlow) {
      subFlowData[action.subFlow] = {
        ...state.dynamic?.[action.flow]?.[action.subFlow],
        [action.key]: {
          ...action.data,
        },
      } as CmsDynamicFeatureCopy;
    } else if (action.subFlow) {
      subFlowData[action.subFlow] = {
        ...state.dynamic?.[action.flow]?.[action.subFlow],
        ...action.data,
      } as CmsDynamicFeatureCopy;
    } else if (action.flow) {
      subFlowData = {
        ...state.dynamic?.[action.flow],
        ...action.data,
      };
    }

    return {
      ...state,
      dynamic: {
        ...state.dynamic,
        [action.flow]: {
          ...state.dynamic?.[action.flow],
          ...subFlowData,
        },
      } as CmsDynamicState,
    };
  }),

  on(
    resetCms,
    (state): State => ({
      ...INITIAL_STATE,
      activeDynamicPage: state.activeDynamicPage
    }),
  ),

  on(
    updateActiveDynamicPage,
    (state, action): State => ({
      ...state,
      activeDynamicPage: action.name,
    }),
  ),
);

export const cmsReducer = (state: State, action: Action): State =>
  reducer(state, action);
